import { useEffect, useState } from "react";
import SubscriptionCard from "./SubscriptionCard";
import { SubscriptionPlan } from "./subscriptionData";
import "./subscription.css";
import { getRequest, postRequest } from "../../api/apiServices";
import apiList from "../../api/apiList";
import Toast from "../../utils/Toast";
import { useNavigate } from "react-router-dom";
import Loader from "../../utils/Loading";
import Popup from "../Common/Popup";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";

const TRUST_ITEMS = [
  "Instant plan switch",
  "Prorated billing",
  "Secure payment",
];

const UpdateSubscriptions = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [planLoading, setPlanLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  const mapApiPlanToSubscriptionPlan = (apiPlan: any): SubscriptionPlan => {
    const isStandard = apiPlan.name?.toLowerCase() === "standard";

    return {
      id: apiPlan.slug || apiPlan.planType || apiPlan.id.toString(),
      name: apiPlan.name,
      subscriptionPlanId: apiPlan.subscriptionPlanId || "",
      price: apiPlan.basePrice,
      period: "/mo",
      features: apiPlan.features?.map((feature: string) => ({
        name: feature,
        included: true,
      })),
      buttonText: "Choose Plan",
      ...(isStandard && {
        badge: "Most Popular",
        isPopular: true,
      }),
    };
  };

  const fetchSubscriptionPlans = async () => {
    try {
      setPlanLoading(true);
      const response: any = await getRequest(apiList.listOfSubscriptions);

      if (response?.data?.success && Array.isArray(response.data.data)) {
        const mappedPlans = response.data.data?.map(
          mapApiPlanToSubscriptionPlan
        );
        setPlans([...mappedPlans]);
      } else {
        console.warn("Invalid or missing plans in response:", response.data);
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    } finally {
      setPlanLoading(false);
    }
  };

  const handlePlanSelection = async (planId: string) => {
    try {
      setPlanLoading(true);
      const result = await postRequest(apiList.updateSubscriptions, {
        subscriptionPlanId: planId,
      });
      if (result?.data?.success) {
        Toast(
          result?.data?.statusMessage ||
            "Subscription plan update successfully..."
        );
        navigate("/billings", { state: { updatedAt: Date.now() } });
      } else {
        console.error("Failed to checkout");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPlanLoading(false);
    }
  };

  const handlePlanClick = (planId: string) => {
    setSelectedPlanId(planId);
    setShowPopup(true);
  };

  const confirmPlanChange = async () => {
    if (!selectedPlanId) return;
    await handlePlanSelection(selectedPlanId);
    setShowPopup(false);
    setSelectedPlanId(null);
  };

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  return planLoading ? (
    <div className="centered-loader">
      <Loader />
    </div>
  ) : (
    <>
      <div className="subscription-page" id="subscription-section">
        {/* Hero */}
        <div className="subscription-hero">
          <div className="subscription-hero-badge">
            <SwapHorizOutlinedIcon sx={{ fontSize: 14 }} />
            Update Plan
          </div>
          <h1 className="subscription-hero-title">
            Upgrade or Change Your Subscription
          </h1>
          <p className="subscription-hero-subtitle">
            Flexible pricing for startups, recruiters, and growing teams.
            Pick the plan that fits where you are today.
          </p>
          <div className="subscription-trust-row">
            {TRUST_ITEMS.map((item) => (
              <span key={item} className="subscription-trust-item">
                <span className="subscription-trust-dot" />
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        <div className="subscription-cards-grid">
          {plans?.map((plan) => (
            <SubscriptionCard
              key={plan.id}
              plan={plan}
              onPlanSelect={() => handlePlanClick(plan.subscriptionPlanId)}
              isLoading={loading}
            />
          ))}
        </div>
      </div>

      <Popup
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        popupName="Confirm Subscription Change"
        popupContain="Are you sure you want to update your subscription plan?"
        onYes={confirmPlanChange}
        onNo={() => setShowPopup(false)}
        yesButtonClass="primary-btn"
        yesButtonText="Yes, Update"
        noButtonText="Cancel"
      />
    </>
  );
};

export default UpdateSubscriptions;
